import * as React from 'react';
import { clsx } from 'clsx';
import { Caption, UIHeading1, UIHeading4 } from '@cian/ui-kit';

import { useFloatingHeader } from '../NewbuildingsComparePage/hooks/useFloatingHeader';
import { TNewbuildingsCompareViewRow } from '../NewbuildingsComparePage';
import * as style from './style.css';

const PIN_FLIP_DURATION_MS = 220;

// useLayoutEffect на сервере предупреждает; FLIP всё равно нужен только в браузере.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export interface INewbuildingsCompareMobilePageProps {
  title: string;
  /** Карточки ЖК (по одной на колонку). Индексы совпадают с индексами ячеек в строках. */
  columns: React.ReactNode[];
  /** Компактные карточки ЖК для плавающей шапки (по одной на колонку, те же индексы). */
  floatingColumns: React.ReactNode[];
  rows: TNewbuildingsCompareViewRow[];
  /** Индекс закреплённой колонки ЖК или null. */
  pinnedIndex: number | null;
}

export const NewbuildingsCompareMobilePage = ({
  title,
  columns,
  floatingColumns,
  rows,
  pinnedIndex,
}: INewbuildingsCompareMobilePageProps) => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const floatingHeaderScrollRef = React.useRef<HTMLDivElement | null>(null);
  const headerRowRef = React.useRef<HTMLDivElement | null>(null);

  const { isFloatingHeaderMounted, isFloatingHeaderVisible, syncFloatingHeaderScroll } = useFloatingHeader({
    floatingHeaderScrollRef,
    headerRowRef,
    scrollRef,
  });

  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      syncFloatingHeaderScroll(event.currentTarget.scrollLeft);
    },
    [syncFloatingHeaderScroll],
  );

  const columnStyle = React.useMemo(() => ({ '--columns': columns.length }) as React.CSSProperties, [columns.length]);

  const cellRefs = React.useRef(new Map<string, HTMLDivElement>());
  const firstLeftsRef = React.useRef(new Map<string, number>());
  const pendingFlipRef = React.useRef(false);

  const registerCell = React.useCallback(
    (id: string) => (node: HTMLDivElement | null) => {
      if (node) {
        cellRefs.current.set(id, node);
      } else {
        cellRefs.current.delete(id);
      }
    },
    [],
  );

  // FLIP-анимация закрепления. Клик по кнопке фиксации ловим в фазе перехвата — до ре-рендера, —
  // и запоминаем текущие левые границы ячеек («First»). Ловим на контейнере, т.к. кнопки приходят
  // внутри opaque-колонок. Снимок именно в момент клика важен: горизонтальный скролл не вызывает
  // ре-рендер, поэтому «прошлые» позиции из предыдущего рендера были бы неактуальны.
  const handlePinToggleCapture = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;

    if (!target || !target.closest('[data-testid="newbuilding-pin-button"]')) {
      return;
    }

    const firstLefts = new Map<string, number>();

    cellRefs.current.forEach((node, id) => {
      firstLefts.set(id, node.getBoundingClientRect().left);
    });

    firstLeftsRef.current = firstLefts;
    pendingFlipRef.current = true;
  }, []);

  // После перестроения раскладки считаем «Last», ставим обратный сдвиг и плавно доезжаем к 0.
  useIsomorphicLayoutEffect(() => {
    if (!pendingFlipRef.current) {
      return undefined;
    }

    pendingFlipRef.current = false;

    // Сбрасываем возможный незавершённый transform от предыдущей анимации, чтобы Last был честным.
    cellRefs.current.forEach(node => {
      node.style.transition = '';
      node.style.transform = '';
    });

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return undefined;
    }

    const animatedNodes: HTMLDivElement[] = [];

    cellRefs.current.forEach((node, id) => {
      const firstLeft = firstLeftsRef.current.get(id);

      if (firstLeft === undefined) {
        return;
      }

      const dx = firstLeft - node.getBoundingClientRect().left;

      if (Math.abs(dx) < 0.5) {
        return;
      }

      node.style.transition = 'none';
      node.style.transform = `translateX(${dx}px)`;
      animatedNodes.push(node);
    });

    if (animatedNodes.length === 0) {
      return undefined;
    }

    // Форсируем reflow, чтобы инвертированный сдвиг применился до включения перехода.
    void scrollRef.current?.offsetWidth;

    const rafId = window.requestAnimationFrame(() => {
      animatedNodes.forEach(node => {
        node.style.transition = `transform ${PIN_FLIP_DURATION_MS}ms ease`;
        node.style.transform = 'translateX(0px)';
      });
    });

    const cleanups = animatedNodes.map(node => {
      const handleEnd = () => {
        node.style.transition = '';
        node.style.transform = '';
        node.removeEventListener('transitionend', handleEnd);
      };

      node.addEventListener('transitionend', handleEnd);

      return () => node.removeEventListener('transitionend', handleEnd);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      cleanups.forEach(cleanup => cleanup());
    };
  }, [pinnedIndex]);

  const renderCell = (content: React.ReactNode, index: number, rowKey: string) => (
    <div
      className={clsx(style['cell'], index === pinnedIndex && style['cell-pinned'])}
      key={index}
      ref={registerCell(`${rowKey}::${index}`)}
      role="cell"
    >
      {content}
    </div>
  );

  const renderHeaderRow = (floating: boolean) => (
    <div className={style['row']} ref={floating ? undefined : headerRowRef} role="row">
      {(floating ? floatingColumns : columns).map((column, index) =>
        renderCell(column, index, floating ? 'floating-header' : 'header'),
      )}
    </div>
  );

  return (
    <div
      className={style['container']}
      data-testid="newbuildings-compare-mobile_page"
      onClickCapture={handlePinToggleCapture}
      style={columnStyle}
    >
      <div className={style['title']}>
        <UIHeading1 as="h1">{title}</UIHeading1>
      </div>

      {isFloatingHeaderMounted && (
        <div
          className={isFloatingHeaderVisible ? style['floating-header-visible'] : style['floating-header']}
          data-testid="FloatingHeader"
        >
          <div className={style['floating-title']}>
            <UIHeading4>{title}</UIHeading4>
          </div>
          <div ref={floatingHeaderScrollRef} className={style['floating-header-scroller']}>
            <div className={style['table']}>{renderHeaderRow(true)}</div>
          </div>
        </div>
      )}

      <div
        ref={scrollRef}
        className={style['table-scroller']}
        data-testid="newbuildings-compare-mobile_table"
        onScroll={handleScroll}
        role="region"
        tabIndex={0}
      >
        <div className={style['table']} role="table">
          {renderHeaderRow(false)}

          {rows.map(row => {
            if (row.kind === 'block') {
              return (
                <div className={style['block-row']} key={row.key} role="row">
                  <UIHeading4>{row.title}</UIHeading4>
                </div>
              );
            }

            if (row.kind === 'action') {
              return (
                <div className={style['full-row']} key={row.key} role="row">
                  {row.content}
                </div>
              );
            }

            return (
              <React.Fragment key={row.key}>
                {row.title && (
                  <div className={style['label-row']} role="row">
                    <Caption color="text-secondary-default">{row.title}</Caption>
                  </div>
                )}
                <div className={style['row']} role="row">
                  {row.cells.map((cell, cellIndex) => renderCell(cell, cellIndex, row.key))}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};
